/**
 * Customer Portal (UI) - account and filter controls.
 *
 * This component keeps account selection and campaign/phone filters visible at
 * the top of the dashboard experience.
 */
import type { AccessibleAccount, Brand, Campaign, PhoneNumber, PortalFilters } from '../../types/portal';

type ScopeControlsProps = {
  accounts: AccessibleAccount[];
  selectedAccountId: string | null;
  canSwitchAccounts: boolean;
  brands: Brand[];
  campaigns: Campaign[];
  phoneNumbers: PhoneNumber[];
  filters: PortalFilters;
  onSelectAccount: (accountId: string) => void;
  onSelectBrand: (brandId: string | null) => void;
  onSelectCampaign: (campaignId: string | null) => void;
  onSelectPhoneNumber: (phoneNumberId: string | null) => void;
  onResetFilters: () => void;
};

function formatAccountTypeLabel(accountType: AccessibleAccount['accountType']): string {
  return accountType
    .split('_')
    .map((part) => (part.length > 0 ? `${part[0].toUpperCase()}${part.slice(1)}` : part))
    .join(' ');
}

export function ScopeControls(props: ScopeControlsProps) {
  return (
    <div className="card mb-3 dashboard-toolbar-card">
      <div className="card-body">
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-4">
            <label className="form-label mb-1">Account</label>
            <select
              className="form-select form-select-sm"
              value={props.selectedAccountId ?? ''}
              disabled={!props.canSwitchAccounts}
              onChange={(event) => props.onSelectAccount(event.target.value)}
            >
              {props.accounts.map((account) => (
                <option value={account.id} key={account.id}>
                  {`${account.name} (${formatAccountTypeLabel(account.accountType)})`}
                </option>
              ))}
            </select>
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label mb-1">Brand</label>
            <select
              className="form-select form-select-sm"
              value={props.filters.brandId ?? ''}
              onChange={(event) => props.onSelectBrand(event.target.value || null)}
            >
              <option value="">All brands</option>
              {props.brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label mb-1">Campaign</label>
            <select
              className="form-select form-select-sm"
              value={props.filters.campaignId ?? ''}
              onChange={(event) => props.onSelectCampaign(event.target.value || null)}
            >
              <option value="">All campaigns</option>
              {props.campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label mb-1">Phone Number</label>
            <select
              className="form-select form-select-sm"
              value={props.filters.phoneNumberId ?? ''}
              onChange={(event) => props.onSelectPhoneNumber(event.target.value || null)}
            >
              <option value="">All phone numbers</option>
              {props.phoneNumbers.map((phone) => (
                <option key={phone.id} value={phone.id}>
                  {phone.e164} ({phone.label})
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-2 d-flex align-items-end">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary w-100"
              onClick={props.onResetFilters}
            >
              Reset filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
